import { useCallback, useEffect, useMemo } from "react";
import { useFetchData } from "../../../../../../../lib/hooks/useDefaultFetchData";
import { useFetchDataCount } from "../../../../../../../lib/hooks/useDefaultFetchDataCount";
import PaymentHistoryService from "../../../../../../../services/accounts/paymentHistory";
import { useGetNestedTableColumns } from "../hooks/useGetNestedTableColumns";
import { useGetTableColumns } from "../hooks/useGetTableColumns";
import { TRequest } from "../types";
import { Table } from "antd";
import { TablePagination } from "../../../../../../../components/TablePagination";
import { useDefualtOnRow } from "../../../../../../../lib/hooks/useDefaultOnRow";
import { Dayjs } from "dayjs";
import { useSorter } from "../hooks/useSorter";

type TPaymentsProps = {
  accountId: string;
  setPaymentId: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  shouldDataReload: boolean;
  setShouldDataReload: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  setIsActionsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  isActionsDisabled: boolean;
  range: [Dayjs, Dayjs];
  reloadAccount: () => void;
};

export const Payments = ({
  accountId,
  setPaymentId,
  shouldDataReload,
  setShouldDataReload,
  setOpenEditMode,
  setIsActionsDisabled,
  isActionsDisabled,
  range,
  reloadAccount,
}: TPaymentsProps) => {
  const {
    total,
    isLoading: isCountLoading,
    onPageChange,
    request,
    fetchCount,
    setRequest,
  } = useFetchDataCount<{
    contractId: string;
    yearFrom: number;
    yearTo: number;
    sort?: number;
  }>(
    PaymentHistoryService,
    {
      contractId: accountId,
      yearFrom: range[0].year(),
      yearTo: range[1].year(),
    },
    ["contractId", "yearFrom", "yearTo"],
    50,
  );

  const { data, isLoading, fetchData } = useFetchData<TRequest, any>(
    PaymentHistoryService,
    request,
    ".payments-table .ant-table-content",
  );

  const reloadData = useCallback(() => {
    fetchCount();
    fetchData();
    reloadAccount();
    setShouldDataReload(false);
  }, [fetchCount, fetchData, setShouldDataReload, reloadAccount]);

  const { onRow } = useDefualtOnRow(setPaymentId);

  const { columns } = useGetTableColumns({
    setPaymentId,
    reloadData,
    setOpenEditMode,
    setIsActionsDisabled,
    isActionsDisabled,
  });

  const { columns: nestedColumns } = useGetNestedTableColumns();

  const expandableRowProps = useMemo(() => {
    return {
      rowExpandable: (record: any) => record.disbursements.length !== 0,
      expandedRowRender: (record: any) => (
        <Table
          columns={nestedColumns}
          dataSource={record.disbursements}
          pagination={false}
          rowKey="id"
          className="payment-history-disb-table mt-3 mb-2 small-table"
          rowClassName={(record) => (record.method === 9 ? "gray-row" : "")}
        />
      ),
    };
  }, [nestedColumns]);

  const tableScroll = useMemo(() => ({ x: "88%" }), []);

  useEffect(() => {
    shouldDataReload && reloadData();
  }, [reloadData, shouldDataReload]);

  const { onSortChange } = useSorter(request, setRequest);

  return (
    <>
      <Table
        columns={columns}
        dataSource={data}
        loading={isLoading}
        pagination={false}
        rowKey="id"
        className="w-100 h-100 clickable-rows-table small-table payments-table"
        expandable={expandableRowProps}
        onRow={onRow}
        scroll={tableScroll}
        onChange={onSortChange}
      />
      {total > 0 && (
        <TablePagination
          total={total}
          isTotalLoading={isCountLoading}
          onPageChange={onPageChange}
          pageSize={request.pageSize}
          currentPage={request.pageNumber}
        />
      )}
    </>
  );
};
